import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { useSortableListContext } from '../utils';

type SortableItem = {
  initialIndex: number;
  currentIndex: number;
};

type UseSortableItemParams = {
  itemType: string;
  initialIndex: number;
};

export const useSortableItem = ({
  itemType,
  initialIndex,
}: UseSortableItemParams) => {
  const sortRef = useRef<HTMLElement | null>(null);
  const { onLocalReorder, onReorderFinished, onReorderStart } =
    useSortableListContext();

  const [{ isDragging }, sortHandleRef, preview] = useDrag(
    () => ({
      type: itemType,
      item: () => {
        onReorderStart?.({ index: initialIndex });
        return { currentIndex: initialIndex, initialIndex };
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        const dropResult: { index: number } | null = monitor.getDropResult();
        if (dropResult != null) {
          onReorderFinished({
            oldIndex: item.initialIndex,
            newIndex: dropResult.index,
          });
        } else {
          onReorderFinished({
            oldIndex: item.initialIndex,
            newIndex: item.initialIndex,
          });
        }
      },
    }),
    [initialIndex, onReorderStart, onReorderFinished],
  );

  const [, drop] = useDrop(
    () => ({
      accept: itemType,
      hover: (draggedItem: SortableItem, monitor) => {
        if (draggedItem.currentIndex !== initialIndex) {
          const dragIndex = draggedItem.currentIndex;
          const hoverIndex = initialIndex;

          // using mouse position and item dimensions, only reorder once the dragged item crosses the middle of the target.
          // this is important when sortable items can vary in height.
          // (see https://github.com/react-dnd/react-dnd/issues/291, https://react-dnd.github.io/react-dnd/examples/sortable/simple)
          const clientOffset = monitor.getClientOffset();
          const hoverBoundingRect = sortRef.current?.getBoundingClientRect();
          if (!hoverBoundingRect || !clientOffset) return;

          const hoverMiddleY =
            (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
          const hoverClientY = clientOffset.y - hoverBoundingRect.top;

          if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
            return;
          }

          if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
            return;
          }

          onLocalReorder({
            oldIndex: draggedItem.currentIndex,
            newIndex: initialIndex,
          });
          draggedItem.currentIndex = initialIndex;
        }
      },
      drop: (draggedItem: SortableItem) => ({
        index: draggedItem.currentIndex,
      }),
    }),
    [initialIndex, onLocalReorder, sortRef],
  );

  preview(drop(sortRef));

  return { sortRef, sortHandleRef, isDragging };
};
